import { useRef, useEffect } from 'react';
import type { GridData, GridCell, Rack, RouteResult } from '../../models/types';
import { render, screenToCell, type RenderState } from '../../services/gridRenderer';

interface GridCanvasProps {
  grid?: GridData;
  racks: Rack[];
  localRackIds?: number[];
  localWallCells?: Set<string>;
  route?: RouteResult | null;
  selectedCells?: Set<string>;
  deletedRackIds?: Set<number>;
  onCellClick?: (cell: GridCell) => void;
  theme?: 'dark' | 'light';
  interactive?: boolean;
}

const EMPTY_SET = new Set<string>();

export const GridCanvas = ({
  grid,
  racks,
  localRackIds = [],
  localWallCells = new Set<string>(),
  route = null,
  selectedCells = EMPTY_SET,
  deletedRackIds = new Set(),
  onCellClick,
  theme = 'dark',
  interactive = true,
}: GridCanvasProps) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const interactionRef = useRef({
    scale: interactive ? 60 : 1,
    panX: 0,
    panY: 0,
    dragging: false,
    didDrag: false,
    lastMouse: { x: 0, y: 0 },
    hoveredCell: null as GridCell | null,
  });

  const propsRef = useRef({ grid, racks, route, selectedCells, deletedRackIds, onCellClick, theme, localWallCells });
  propsRef.current = { grid, racks, route, selectedCells, deletedRackIds, onCellClick, theme, localWallCells };

  // Track previous grid dimensions to detect local changes (e.g. rows/cols override)
  const prevDimsRef = useRef<{ cols: number; rows: number } | null>(null);

  const drawRef = useRef(() => {});
  drawRef.current = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const {
      grid: g,
      racks: r,
      route: rt,
      selectedCells: sel,
      deletedRackIds: del,
      theme: th,
      localWallCells: lwc,
    } = propsRef.current;
    const byPos = new Map<string, Rack>();
    const byId = new Map<number, Rack>();
    for (const rack of r) {
      byPos.set(`${rack.row}:${rack.col}`, rack);
      byId.set(rack.id, rack);
    }

    const s = interactionRef.current;
    const renderState: RenderState = {
      grid: g,
      racksById: byId,
      racksByPos: byPos,
      route: rt,
      selectedCells: sel,
      deletedRackIds: del,
      localRackIds: localRackIds,
      scale: s.scale,
      panX: s.panX,
      panY: s.panY,
      hoveredCell: s.hoveredCell,
      theme: th,
      localWallCells: lwc,
    };
    render(ctx, canvas.width, canvas.height, renderState);
  };

  const draw = () => drawRef.current();

  useEffect(() => {
    draw();
  }, [grid, racks, route, selectedCells, deletedRackIds, localWallCells, theme]);

  // Reset scale/pan when grid dimensions change (local preview)
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !interactive) return;
    const g = propsRef.current.grid;
    if (!g) return;

    const container = canvas.parentElement;
    if (!container) return;
    const rect = container.getBoundingClientRect();
    if (rect.width === 0 || rect.height === 0) return;

    const padding = 20;
    const scaleX = (rect.width - padding * 2) / g.grid_cols;
    const scaleY = (rect.height - padding * 2) / g.grid_rows;
    const newScale = Math.min(scaleX, scaleY);

    const prev = prevDimsRef.current;
    if (!prev || prev.cols !== g.grid_cols || prev.rows !== g.grid_rows) {
      const s = interactionRef.current;
      s.scale = newScale;
      s.panX = 0;
      s.panY = 0;
      prevDimsRef.current = { cols: g.grid_cols, rows: g.grid_rows };
      draw();
    }
  }, [grid, interactive]);

  // Resize observer + fit-to-container when non-interactive
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const container = canvas.parentElement;
    if (!container) return;

    const resize = () => {
      const rect = container.getBoundingClientRect();
      if (rect.width === 0 || rect.height === 0) return;
      canvas.width = rect.width;
      canvas.height = rect.height;
      if (!interactive) {
        const g = propsRef.current.grid;
        const padding = 20;
        const scaleX = (rect.width - padding * 2) / g.grid_cols;
        const scaleY = (rect.height - padding * 2) / g.grid_rows;
        const s = interactionRef.current;
        s.scale = Math.min(scaleX, scaleY);
        s.panX = 0;
        s.panY = 0;
      }
      draw();
    };

    const observer = new ResizeObserver(resize);
    observer.observe(container);
    resize();

    return () => observer.disconnect();
  }, [interactive]);

  // Pan, zoom, hover — only when interactive
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !interactive) {
      return;
    }

    const onMouseDown = (e: MouseEvent) => {
      const s = interactionRef.current;
      s.dragging = true;
      s.didDrag = false;
      s.lastMouse = { x: e.clientX, y: e.clientY };
      canvas.style.cursor = 'grabbing';
    };

    const onMouseMove = (e: MouseEvent) => {
      const s = interactionRef.current;
      if (s.dragging) {
        const dx = e.clientX - s.lastMouse.x;
        const dy = e.clientY - s.lastMouse.y;
        if (Math.abs(dx) > 2 || Math.abs(dy) > 2) s.didDrag = true;
        s.panX += dx;
        s.panY += dy;
        s.lastMouse = { x: e.clientX, y: e.clientY };
        draw();
      }

      const { grid: g } = propsRef.current;
      if (g) {
        const rect = canvas.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;
        const { gx, gy } = screenToCell(
          x,
          y,
          canvas.width,
          canvas.height,
          g.grid_cols,
          g.grid_rows,
          s.scale,
          s.panX,
          s.panY,
        );
        const cell = g.cells.find((c) => c.x === gx && c.y === gy) ?? null;
        if (cell !== s.hoveredCell) {
          s.hoveredCell = cell;
          canvas.style.cursor = cell?.type === 'rack' ? 'pointer' : 'grab';
          draw();
        }
      }
    };

    const onMouseUp = () => {
      const s = interactionRef.current;
      if (!s.didDrag && s.hoveredCell) {
        propsRef.current.onCellClick?.(s.hoveredCell);
      }
      s.dragging = false;
      canvas.style.cursor = s.hoveredCell?.type === 'rack' ? 'pointer' : 'grab';
    };

    const onMouseLeave = () => {
      const s = interactionRef.current;
      s.dragging = false;
      s.hoveredCell = null;
      canvas.style.cursor = 'grab';
      draw();
    };

    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      const s = interactionRef.current;
      const factor = e.deltaY > 0 ? 0.9 : 1.1;
      const rect = canvas.getBoundingClientRect();
      const mx = e.clientX - rect.left - canvas.width / 2 - s.panX;
      const my = e.clientY - rect.top - canvas.height / 2 - s.panY;
      s.panX -= mx * (factor - 1);
      s.panY -= my * (factor - 1);
      s.scale *= factor;
      draw();
    };

    canvas.addEventListener('mousedown', onMouseDown);
    canvas.addEventListener('mousemove', onMouseMove);
    canvas.addEventListener('mouseup', onMouseUp);
    canvas.addEventListener('mouseleave', onMouseLeave);
    canvas.addEventListener('wheel', onWheel, { passive: false });

    return () => {
      canvas.removeEventListener('mousedown', onMouseDown);
      canvas.removeEventListener('mousemove', onMouseMove);
      canvas.removeEventListener('mouseup', onMouseUp);
      canvas.removeEventListener('mouseleave', onMouseLeave);
      canvas.removeEventListener('wheel', onWheel);
    };
  }, [interactive]);

  return (
    <div className="relative w-full h-full">
      <canvas
        ref={canvasRef}
        style={{ display: 'block', cursor: interactive ? 'grab' : 'default', maxWidth: '100%', height: 'auto' }}
      />
    </div>
  );
};
